/**
 * 2026 Song Leader Ring — TSU-style options (Metal / Stone / Size).
 * - Full variant map via song-leader-variants.json (product.js caps at 250)
 * - Mesh3D color hot-swap (postMessage) like preset-3d / seals
 * - Lettering contrast via flat{tone} (seal part2tone2 pattern)
 */
(() => {
  const root = document.querySelector('[data-song-leader]');
  if (!root) return;

  const metalEl = root.querySelector('[data-sl-metal]');
  const stoneEl = root.querySelector('[data-sl-stone]');
  const sizeEl = root.querySelector('[data-sl-size]');
  const priceEl = root.querySelector('[data-sl-price]');
  const atcEl = root.querySelector('[data-sl-atc]');
  const statusEl = root.querySelector('[data-sl-status]');
  const metalSummary = root.querySelector('[data-sl-metal-summary]');
  const stoneSummary = root.querySelector('[data-sl-stone-summary]');
  const sizeSummary = root.querySelector('[data-sl-size-summary]');

  const meshBase =
    root.dataset.meshBase ||
    'https://portal.mesh3d.com/embed/186f48af-e85c-4bfa-96b0-46b4a9b520c2?client=sanjose&part1=busl';
  const productId = root.dataset.productId || '';
  const variantsUrl = root.dataset.variantsUrl || '';
  const PART2_PREFIX = 'flat';

  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });

  /** Fallback metal → cents (stone color never changes price). */
  const METAL_PRICES = {
    'Platinum Silver': 10000,
    '10K White Gold': 30000,
    '10K Yellow Gold': 30000,
    '10K Rose Gold': 30000,
    '14K White Gold': 40000,
    '14K Yellow Gold': 40000,
    '14K Rose Gold': 40000,
  };

  let variants = [];
  let metalPrices = { ...METAL_PRICES };
  let byKey = new Map();
  let current = null;
  let lastAppliedSrc = '';
  let viewerReady = false;

  function meshMetal(label) {
    const s = String(label || '').toLowerCase();
    if (s.includes('yellow')) return 'yellow';
    if (s.includes('rose')) return 'rose';
    return 'white';
  }

  function meshStone(label) {
    const map = {
      white: 'white',
      ruby: 'ruby',
      garnet: 'garnet',
      blue: 'blue',
      green: 'green',
      black: 'black',
      orange: 'orange',
      yellow: 'yellow',
      purple: 'purple',
    };
    return map[String(label || 'white').toLowerCase().trim()] || 'white';
  }

  function keyOf(metal) {
    return String(metal || '');
  }

  function findIframes() {
    const id = `3dviewer_${productId}`;
    const all = [...document.querySelectorAll(`[id="${id}"]`)];
    if (!all.length) return [];
    const visible = all.filter((el) => {
      try {
        return el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null;
      } catch (_) {
        return true;
      }
    });
    return visible.length ? visible : all;
  }

  function portalSrc(src) {
    try {
      const u = new URL(src, window.location.href);
      ['part1', 'part2', 'part3', 'part4'].forEach((k) => {
        const v = u.searchParams.get(k);
        if (v) u.searchParams.set(k, v.toLowerCase());
      });
      return u.toString();
    } catch (_) {
      return src;
    }
  }

  function buildSrc(mc, sc) {
    const tone2 = `${PART2_PREFIX}${mc}`;
    return (
      meshBase +
      `&part1Color=${encodeURIComponent(mc)}` +
      `&part2Color=${encodeURIComponent(mc)}` +
      `&part3Color=${encodeURIComponent(mc)}` +
      `&part4Color=${encodeURIComponent(mc)}` +
      `&part2tone2=${encodeURIComponent(tone2)}` +
      `&sealTone2=${encodeURIComponent(mc)}` +
      `&part1g1=${encodeURIComponent(sc)}`
    );
  }

  /** Same hot-swap shape as preset-3d / kiosk — tone2 = recessed contrast. */
  function buildViewerPayload(mc, sc) {
    const tone2 = `${PART2_PREFIX}${mc}`;
    return {
      autorotate: false,
      zoom: true,
      parts: {
        part1: {
          part: 'busl',
          colors: {
            tone1: mc,
            tone2,
            gems: { g1: sc },
          },
        },
        part2: {
          part: '',
          colors: { tone1: mc, tone2 },
        },
        part3: { part: '' },
        part4: { part: '', color: '' },
      },
      sealTone2: mc,
    };
  }

  function postToViewer(iframe, data) {
    if (!iframe?.contentWindow) return;
    try {
      iframe.contentWindow.postMessage(data, '*');
    } catch (err) {
      console.warn('[song-leader] postMessage failed', err);
    }
  }

  function setIframeSrc(iframe, src) {
    const applied = portalSrc(src);
    iframe.setAttribute('data-src', applied);
    iframe.src = applied;
    iframe.classList.add('loaded');
    return applied;
  }

  function syncViewer() {
    const mc = meshMetal(metalEl?.value);
    const sc = meshStone(stoneEl?.value);
    const src = buildSrc(mc, sc);
    const iframes = findIframes();
    if (!iframes.length) return;

    const appliedPrev = lastAppliedSrc;
    const needsReload = !viewerReady || !appliedPrev;
    // Color / stone only — hot-swap preserves camera (same as preset-3d)
    if (needsReload) {
      let applied = src;
      iframes.forEach((iframe) => {
        applied = setIframeSrc(iframe, src);
      });
      lastAppliedSrc = applied;
      viewerReady = true;
      return;
    }

    const payload = buildViewerPayload(mc, sc);
    const applied = portalSrc(src);
    iframes.forEach((iframe) => {
      iframe.setAttribute('data-src', applied);
      postToViewer(iframe, payload);
      postToViewer(iframe, { type: 'assembleItems' });
    });
    lastAppliedSrc = applied;
  }

  function findVariant() {
    const metal = metalEl?.value || '';
    if (!metal) return null;
    return byKey.get(keyOf(metal)) || null;
  }

  function priceCents() {
    if (current && typeof current.price === 'number') return current.price;
    const metal = metalEl?.value || '';
    return metalPrices[metal] || METAL_PRICES[metal] || null;
  }

  function setSummaries() {
    if (metalSummary) metalSummary.textContent = metalEl?.value || '';
    if (stoneSummary) stoneSummary.textContent = stoneEl?.value || '';
    if (sizeSummary) sizeSummary.textContent = sizeEl?.value || '';
  }

  function refresh() {
    setSummaries();
    syncViewer();
    current = findVariant();
    const cents = priceCents();
    if (priceEl) {
      priceEl.textContent = cents != null ? money.format(cents / 100) : '';
    }
    if (atcEl) {
      const hasSize = Boolean(sizeEl?.value);
      const ok = !!(current && hasSize && current.available !== false);
      atcEl.disabled = !ok;
      if (!hasSize) atcEl.textContent = 'Select ring size';
      else if (!current) atcEl.textContent = 'Unavailable';
      else if (current.available === false) atcEl.textContent = 'Sold out';
      else atcEl.textContent = 'Add to Bag';
    }
    if (statusEl) {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
    const idInput = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (idInput && current) idInput.value = String(current.id);
  }

  async function loadVariants() {
    if (!variantsUrl) throw new Error('missing variants url');
    const res = await fetch(variantsUrl, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('variants.json ' + res.status);
    const data = await res.json();
    variants = data.variants || [];
    if (data.metal_prices) metalPrices = { ...METAL_PRICES, ...data.metal_prices };
    byKey = new Map(variants.map((v) => [keyOf(v.metal), v]));
    console.log('[song-leader] loaded variants', variants.length);
    refresh();
  }

  async function addToCart() {
    current = findVariant();
    if (!current || !sizeEl?.value) {
      if (statusEl) {
        statusEl.hidden = false;
        statusEl.textContent = 'Please select a ring size.';
      }
      return;
    }
    atcEl.disabled = true;
    atcEl.textContent = 'Adding…';
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: current.id,
              quantity: 1,
              properties: {
                _song_leader: '2026',
                Metal: metalEl.value,
                'Stone Color': stoneEl.value,
                'Ring Size': sizeEl.value,
              },
            },
          ],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Add to cart failed');
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
      window.dispatchEvent(new CustomEvent('cart:updated'));
      if (typeof window.refreshCart === 'function') window.refreshCart();
      const drawer = document.querySelector('cart-drawer, [data-cart-drawer], .cart-drawer');
      if (!drawer) {
        window.location.href = '/cart';
        return;
      }
      if (drawer.open) drawer.open();
      else document.dispatchEvent(new CustomEvent('cart:open'));
      if (statusEl) {
        statusEl.hidden = false;
        statusEl.textContent = 'Added to bag.';
      }
    } catch (e) {
      if (statusEl) {
        statusEl.hidden = false;
        statusEl.textContent = e.message || 'Could not add to bag.';
      }
    } finally {
      refresh();
    }
  }

  metalEl?.addEventListener('change', refresh);
  stoneEl?.addEventListener('change', refresh);
  sizeEl?.addEventListener('change', refresh);
  atcEl?.addEventListener('click', (e) => {
    e.preventDefault();
    addToCart();
  });

  document.querySelector('.product')?.setAttribute('data-song-leader-pdp', 'true');

  loadVariants().catch((e) => {
    console.error('[song-leader]', e);
    // Still show metal price + hot-swap even if catalog fails
    refresh();
    if (statusEl) {
      statusEl.hidden = false;
      statusEl.textContent = 'Could not load all options — try refreshing.';
    }
  });
})();
